/** @jsxImportSource @emotion/react */
import { Buttons, Dialogs } from '@zip/business-components';
import { Constants } from 'global';
import { FC } from 'react';
import { currencyFormatter } from 'utils';
import * as styles from './ZipPlusMMPDialog.styles';
import { RepaymentCalculations } from 'types';

const ZipPlusMMPDialog: FC<{
  open: boolean;
  onClose: () => void;
  selectedQuote: RepaymentCalculations;
  zipPlusNewCalculatorEnabled: boolean;
}> = ({ open, onClose, selectedQuote, zipPlusNewCalculatorEnabled }) => {
  const zipPlusDefaults = Constants.productDefaults['Zip Plus'];

  const minimumMonthlyRepaymentValue = zipPlusNewCalculatorEnabled
    ? (selectedQuote?.minimumMonthlyRepayment ??
      zipPlusDefaults.minimumMonthlyRepayment)
    : zipPlusDefaults.minimumMonthlyRepayment;

  const minimumMonthlyRepaymentPercentage = zipPlusNewCalculatorEnabled
    ? (selectedQuote?.minimumMonthlyRepaymentPercentage ??
      zipPlusDefaults.minimumMonthlyRepaymentPercentage)
    : zipPlusDefaults.minimumMonthlyRepaymentPercentage;

  const minimumMonthlyRepayment = currencyFormatter(
    minimumMonthlyRepaymentValue,
    false
  );

  return (
    <Dialogs.Basic
      id="zipPlusMMPDialog"
      onClose={onClose}
      open={open}
      showClose={false}
      scroll="paper"
      css={styles.dialog}
      actions={
        <Buttons.Primary onClick={onClose}>Okay, got it</Buttons.Primary>
      }
    >
      <div css={styles.dialogContent}>
        <p className="title3" css={styles.title}>
          Minimum monthly repayment
        </p>

        <div css={styles.sections}>
          <section css={styles.section}>
            <p className="heading3" css={styles.sectionTitle}>
              How is the minimum calculated?
            </p>
            <div css={styles.sectionContent}>
              <p className="body2" css={styles.bodyCopy}>
                The minimum monthly repayments are determined by the previous
                months statement closing balance. It&apos;s the{' '}
                <strong>
                  higher of {minimumMonthlyRepayment} or{' '}
                  {minimumMonthlyRepaymentPercentage}%
                </strong>{' '}
                of the closing balance.
              </p>

              <div css={styles.chartPanel}>
                <div css={styles.chartBubble}>
                  <p className="body1" css={styles.chartBubbleText}>
                    Minimum monthly repayment:{' '}
                    <span> {minimumMonthlyRepayment} </span>
                  </p>
                </div>

                <div css={styles.chartCanvas}>
                  <div css={styles.chartGuideLine} />
                  <div css={styles.chartConnector} />
                  <div css={styles.chartConnectorDot} />
                  <div css={styles.chartBaseline} />

                  <div css={styles.chartColumns}>
                    <div css={styles.chartColumn}>
                      <div css={styles.fixedBar} />
                      <p className="body1" css={styles.chartLabel}>
                        {minimumMonthlyRepayment}
                      </p>
                      <p className="heading3" css={styles.chartSubLabel}>
                        (fixed)
                      </p>
                    </div>

                    <div css={styles.chartColumn}>
                      <div css={styles.percentageBar} />
                      <p className="body1" css={styles.chartLabel}>
                        {minimumMonthlyRepaymentPercentage}% of
                      </p>
                      <p className="heading3" css={styles.chartSubLabel}>
                        closing balance
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section css={styles.section}>
            <p className="heading3" css={styles.sectionTitle}>
              When is the minimum calculated?
            </p>
            <p className="body2" css={styles.bodyCopy}>
              The previous months statement closing balance is generated on the
              first day of the month. This is when customers will see the
              minimum due for the month in the app.
            </p>
          </section>

          <section css={styles.section}>
            <p className="heading3" css={styles.sectionTitle}>
              What is the impact of repaying the minimum?
            </p>
            <p className="body2" css={styles.bodyCopy}>
              Repaying the minimum will take longer to clear the balance and
              customers may pay more in interest and fees. Consider higher
              repayments to repay the balance faster.
            </p>
          </section>

          <section css={styles.section}>
            <p className="heading3" css={styles.sectionTitle}>
              Why does the minimum change?
            </p>
            <p className="body2" css={styles.bodyCopy}>
              If the scheduled repayments are not sufficient to pay the minimum
              monthly repayment in full by the due date, we will automatically
              increase the scheduled payments to meet that amount. Where the
              minimum monthly repayments increase or decrease in later months,
              we will adjust the scheduled repayments accordingly.
            </p>
          </section>
        </div>
      </div>
    </Dialogs.Basic>
  );
};

export default ZipPlusMMPDialog;
